"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.publish = exports.restore = exports.validate = void 0;
const debugModule = require("debug");
const errors_1 = require("../../errors");
const path = require("path");
const subprocess = require("./subprocess");
const debug = debugModule('snyk');
async function handle(operation, command, args) {
    debug(`running dotnet command: ${operation}: ${command}`);
    try {
        return await subprocess.execute(command, args);
    }
    catch (error) {
        if (!(typeof error === 'object' &&
            error !== null &&
            'stdout' in error &&
            'stderr' in error)) {
            throw new errors_1.CliCommandError(`dotnet ${operation} failed with error: ${error}`);
        }
        const message = error.stdout || error.stderr;
        throw new errors_1.CliCommandError(`dotnet ${operation} failed with error: ${message}`);
    }
}
async function validate() {
    const command = 'dotnet';
    const args = ['--version'];
    try {
        await handle('version', command, args);
    }
    catch (error) {
        debug('dotnet tool not found, did you install dotnet core?');
        throw error;
    }
}
exports.validate = validate;
async function restore(projectPath) {
    const command = 'dotnet';
    const args = ['restore', '--no-cache', projectPath];
    await handle('restore', command, args);
    return;
}
exports.restore = restore;
async function publish(projectPath) {
    var _a;
    const command = 'dotnet';
    const args = ['publish', '--nologo'];
    // Self-contained: Create all required .dlls for version investigation, don't rely on the environment.
    args.push('--sc');
    // The path that contains either some form of project file, or a .sln one.
    // See: https://learn.microsoft.com/en-us/dotnet/core/tools/dotnet-publish#arguments
    args.push(projectPath);
    const response = await handle('publish', command, args);
    // The default output folder is [project_file_folder]/bin/[configuration]/[framework]/[runtime]/publish/
    // for a self-contained executable. Specifically determining an output folder with --output is deprecated,
    // as it leads to unpredictable results for multiple target frameworks and runtimes, so we have to cherry-pick
    // the folders.
    // See: https://learn.microsoft.com/en-us/dotnet/core/compatibility/sdk/7.0/solution-level-output-no-longer-valid.
    // FIXME: As we're not supporting multiple frameworks all over the place, we have to just take the first one.
    //  It's probably safe, as runtime dll versions most likely don't differ much across *most* dependencies, but
    //  OS-specific ones (e.g. crypto) might.
    // Looking for something like <project_name> -> /path/to/<project_name>/bin/Debug/<target_framework>/publish/ on Unix.
    // We could also just hope Microsoft never changes their naming convention, but I think this is less error-prone,
    // and will potentially also support multiple target frameworks.
    const publishDirLine = response.stdout
        .split(/[\r\n]+/)
        // TODO: For multiple target frameworks, replace `find` with a map or something of that kind to return more than the first.
        // The first thing to get published ought to be the project's own .dll or .exe file, depending on the architecture.
        // E.g., something like:
        // dotnet_6 -> /foo/bar/project/bin/Debug/net6.0/osx-arm64/project_name.dll
        // Either way, since we're forcing a publish of a self-contained project, all .dlls should be placed there.
        // This logic does seem a bit popsicle and duct-tape ish, but I have yet to find a more stable solution. PRs welcome!
        .find((line) => line.endsWith('.dll') || line.endsWith('.exe'));
    if (!publishDirLine) {
        const err = `Could not find a valid publish path while reading stdout: ${response.stdout}`;
        debug(err);
        throw new errors_1.CliCommandError(`Unable to find a publish dir: ${err}`);
    }
    // dotnet_6 -> /foo/bar/project/bin/Debug/net6.0/osx-arm64/project_name.dll will then have the first part removed:
    const [, publishedDllPath] = (_a = publishDirLine.split('->')) !== null && _a !== void 0 ? _a : [];
    if (!publishedDllPath) {
        const err = `Could not find a valid publish dir while splitting the line: ${publishDirLine}`;
        debug(err);
        throw new errors_1.CliCommandError(`Unable to find a publish dir: ${err}`);
    }
    // /foo/bar/project/bin/Debug/net6.0/osx-arm64/project_name.dll will then need to be stripped from a file name,
    // in order to return just the so-called "publish dir":
    const dirName = path.dirname(publishedDllPath.trim());
    return dirName;
}
exports.publish = publish;
//# sourceMappingURL=dotnet.js.map